<div cdkDrag cdkDragRootElement=".cdk-overlay-pane" cdkDragBoundary=".cdk-overlay-container">
  <div cdkDragHandle class="flex justify-between items-baseline pr-4">
    <h2 mat-dialog-title>Lead Details@if (lead_details) {
      &nbsp;&dash;&nbsp;{{ lead_details?.fname }}&nbsp;{{ lead_details?.lname }}
    }</h2>
    @if (!isLoading) {
      <button type="button" mat-icon-button class="leading-none" mat-dialog-close>
        <mat-icon>close</mat-icon>
      </button>
    }
  </div>
  @if (!isLoading) {
    <div class="flex items-center gap-2 px-4 mb-2">
      <app-lead-last-updated [lead_id]="lead_id"></app-lead-last-updated>
      <button mat-icon-button type="button" class="leading-none" matTooltip='If changes are submitted via Client Self-Service, the date of the most recent change to client information will display in this box. When the box is expanded, fields with changed values will show. If the "Confirm and Save" button is clicked in Client Self-Service and no change is made to the client&apos;s information, then the date and changes displayed in this expandable box will reflect changes as of the date of the most recent change, which may be in the past. See Conversation Log below to confirm most recent date client completed review of their information, even if they made no changes.'>
        <mat-icon class="text-emerald-800">help</mat-icon>
      </button>
    </div>
  }

  <mat-divider></mat-divider>

  <div mat-dialog-content>
    @if (message.type) {
      <app-sms-notice-banner #messageBanner [type]="message.type" (closeBanner)="clearMessage(0)">
        <p>{{ message.text }}</p>
      </app-sms-notice-banner>
    }

    @if (isLoading) {
      <div class="mx-auto w-96 h-32 loading-indicator">
        <app-sms-spinner></app-sms-spinner>
      </div>
    } @else {
      @if (hasChange) {
        <p class="update_msg">Changes have been made. Select <a class="custom-link" title="Update Lead Detail Changes" (click)="updateLead('updateOnly')"><strong>"Update"</strong></a> to save them.</p>
      }
      <form class="flex justify-between gap-2" autocomplete="off" [formGroup]="leadDetailsForm">
        <!-- Left Column -->
        <div class="w-4/6">
          <!-- Lead Basic Info -->
          <div>
            <h4 class="user-profile-heading">Lead Information</h4>
            <div class="flex gap-2">
              <mat-form-field class="w-20">
                <mat-label>Prefix</mat-label>
                <input matInput formControlName="title" [maxlength]="6">
              </mat-form-field>
              <mat-form-field class="grow">
                <mat-label>First Name</mat-label>
                <input matInput formControlName="fname" [maxlength]="25" appDisableAutofill>
                @if (leadDetailsForm.value.fname.length == 25) {
                  <mat-hint>Max length is 25 characters</mat-hint>
                }
              </mat-form-field>
              <mat-form-field class="w-20">
                <mat-label>Initial</mat-label>
                <input matInput formControlName="mname" [maxlength]="1" (input)="filterAlphaCharacters($event)">
                @if (leadDetailsForm.get('mname')?.hasError('pattern')) {
                  <mat-error></mat-error>
                }
              </mat-form-field>
              <mat-form-field class="grow">
                <mat-label>Last Name</mat-label>
                <input matInput formControlName="lname" [maxlength]="25" appDisableAutofill>
                @if (leadDetailsForm.value.lname.length == 25) {
                  <mat-hint>Max length is 25 characters</mat-hint>
                }
              </mat-form-field>
              <mat-form-field class="grow mat-label-with-icon">
                <mat-label>Nickname</mat-label>
                <button mat-icon-button type="button" class="p-0 medicare-tooltip-btn" matTooltip='The nickname field should be used for the lead’s preferred name. The nickname will display to the lead in Client Self-Service. Nickname will be part of the client export but will NOT carry over to enrollment forms or SOAs.'>
                  <mat-icon class="text-emerald-800 small-icon">help</mat-icon>
                </button>
                <input matInput formControlName="nickname" [maxlength]="25" appDisableAutofill>
                @if (leadDetailsForm.value.nickname?.length == 25) {
                  <mat-hint>Max length is 25 characters</mat-hint>
                }
              </mat-form-field>
            </div>
            <div class="flex gap-2">
              <mat-form-field class="grow">
                <mat-label>Birthdate</mat-label>
                <input matInput formControlName="dob" [max]="maxDate" appDate>
                @if (checkError('dob', 'invalidDate')) {
                  <mat-error>You must enter a valid date.</mat-error>
                }
              </mat-form-field>
              <mat-form-field class="w-1/5">
                <mat-label>Gender</mat-label>
                <mat-select formControlName="gender" panelWidth="">
                  @for (gender of gender_data; track gender) {
                    <mat-option value="{{gender.value}}">{{gender.text}}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-form-field class="w-1/5">
                <mat-label>Marital Status</mat-label>
                <mat-select formControlName="marital_status" panelWidth="">
                  @for (marital of marital_data; track marital) {
                    <mat-option value="{{marital.value}}">{{marital.text}}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <div class="flex gap-2 w-36">
                <mat-form-field class="w-1/2">
                  <mat-label>Height</mat-label>
                  <input matInput title="Height in feet" formControlName="height_feet" (input)="checkFeetHeight($event.target)" appNumbersOnly>
                  <span matTextSuffix>ft</span>
                </mat-form-field>
                <mat-form-field class="w-1/2">
                  <mat-label></mat-label>
                  <input matInput title="Height in inches" formControlName="height_inches" (input)="checkInchesHeight($event.target)" appNumbersOnly>
                  <span matTextSuffix>in</span>
                </mat-form-field>
              </div>
              <mat-form-field class="w-28">
                <mat-label>Weight</mat-label>
                <input matInput formControlName="weight" (input)="checkWeight($event.target)" appNumbersOnly>
                <span matTextSuffix>lbs</span>
              </mat-form-field>
              <mat-form-field class="grow">
                <mat-label>SSN</mat-label>
                <input matInput formControlName="ssn" [maxLength]="11" appSocialSecurityMask>
              </mat-form-field>
            </div>
          </div>
          <!-- Lead Phone number list -->
          @if (isCommonFlag) {
            <app-phone formArrayName="phones" type="lead"></app-phone>
          }
          <!-- Lead Email list -->
          @if (isCommonFlag || !isLoading) {
            <app-email formArrayName="emails" type="lead"></app-email>
          }
          <!-- Lead Address list -->
          @if (isCommonFlag) {
            <app-address formArrayName="addresses" type="lead"></app-address>
          }
          <div>
            <!-- Lead Preferred contact -->
            <h4 class="flex w-full user-profile-heading">Preferred Contact</h4>
            <div class="flex gap-2 place-items-center">
              <mat-form-field class="grow">
                <mat-label>Name</mat-label>
                <input matInput formControlName="contact_name" [maxlength]="50" appDisableAutofill>
                @if (leadDetailsForm.value.contact_name.length == 50) {
                  <mat-hint>Max length is 50 characters</mat-hint>
                }
              </mat-form-field>
              <mat-form-field class="grow-0">
                <mat-label>Type</mat-label>
                <mat-select formControlName="contact_phone_type">
                  @for (contact of contact_data; track contact) {
                    <mat-option value="{{contact.value}}">{{contact.text}}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-form-field class="grow">
                <mat-label>Telephone #</mat-label>
                <input matInput formControlName="contact_phone" appPhoneNumberMask [maxlength]="14" appDisableAutofill>
              </mat-form-field>
              <mat-form-field class="grow">
                <mat-label>Relationship</mat-label>
                <mat-select formControlName="contact_relationship">
                  @for (relation of relation_data; track relation) {
                    <mat-option value="{{relation.value}}">{{relation.text}}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <button mat-icon-button class="close" title="Clear Preferred Contact" (click)="clearPreferredContact()">
                <mat-icon>close</mat-icon>
              </button>
            </div>
            <div class="flex gap-2 w-full place-items-center">
              <label>Use as emergency contact?</label>
              <mat-radio-group formControlName="isEmergencyContact" (change)="radioChange($event)">
                <mat-radio-button value="1">Yes</mat-radio-button>
                <mat-radio-button value="0">No</mat-radio-button>
              </mat-radio-group>
            </div>
            @if (isEmergency) {
              <!-- Lead Emergency contact -->
              <h4 class="user-profile-heading">Emergency Contact</h4>
              <div class="flex gap-2 place-items-center">
                <mat-form-field class="grow">
                  <mat-label>Name</mat-label>
                  <input matInput formControlName="emergency_name" [maxlength]="50" appDisableAutofill>
                  @if (leadDetailsForm.value.emergency_name.length == 50) {
                    <mat-hint>Max length is 50 characters</mat-hint>
                  }
                </mat-form-field>
                <mat-form-field class="grow-0">
                  <mat-label>Type</mat-label>
                  <mat-select formControlName="emergency_phone_type">
                    @for (contact of contact_data; track contact) {
                      <mat-option value="{{contact.value}}">{{contact.text}}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                <mat-form-field class="grow">
                  <mat-label>Telephone #</mat-label>
                  <input matInput formControlName="emergency_phone" appPhoneNumberMask [maxlength]="14" appDisableAutofill>
                </mat-form-field>
                <mat-form-field class="grow">
                  <mat-label>Relationship</mat-label>
                  <mat-select formControlName="emergency_relationship">
                    @for (relation of relation_data; track relation) {
                      <mat-option value="{{relation.value}}">{{relation.text}}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                <button mat-icon-button class="close" title="Clear Emergency Contact" (click)="clearEmergencyContact()">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
          </div>
        </div>
        <!-- Right Column -->
        <div class="w-2/6">
          <!-- Lead Medicare Card -->
          <h4 class="user-profile-heading text-upper">Medicare & Medicaid</h4>
          <div class="flex gap-4">
            <mat-form-field class="grow">
              <mat-label>Medicare Name</mat-label>
              <input matInput
                matTooltip="{{(getMedicareName()).length > 50?(getMedicareName()):''}}"
                [value]="getMedicareName()"  readonly>
              </mat-form-field>
              <mat-form-field class="grow mat-label-with-icon">
                <mat-label>Medicare Number </mat-label>
                <button mat-icon-button type="button" class="p-0 medicare-tooltip-btn" matTooltipClass="medicare-tooltip" [matTooltip]="getTooltipText()">
                  <mat-icon class="text-emerald-800 small-icon">help</mat-icon>
                </button>
                <input matInput #mbiInput formControlName="hicn" (input)="formatMBI()" [maxlength]="13">
                @if (leadDetailsForm.value.hicn?.length > 0 && leadDetailsForm.value.hicn?.length < 14) {
                  <mat-hint>Max length is 11 characters</mat-hint>
                }
                @if (leadDetailsForm.get('hicn')?.hasError('invalidLength')) {
                  <mat-error>Medicare number must be 11 characters long.</mat-error>
                }
                @if (leadDetailsForm.get('hicn')?.hasError('invalidFormat')) {
                  <mat-error>Medicare number contains invalid characters.</mat-error>
                }
              </mat-form-field>
            </div>
            <div class="flex gap-2">
              <mat-form-field class="grow">
                <mat-label>Part A Date</mat-label>
                <input matInput formControlName="medicare_parta_date" placeholder="MM/YYYY" (input)="formatMMYYYY($event,'medicare_parta_date')" maxlength="7" [min]="partAB_MinDate">
                @if (leadDetailsForm.get('medicare_parta_date')?.invalid) {
                  <mat-error>Part A date can't be before 1965</mat-error>
                }
              </mat-form-field>
              <mat-form-field class="grow">
                <mat-label>Part B Date</mat-label>
                <input matInput formControlName="medicare_partb_date" placeholder="MM/YYYY" (input)="formatMMYYYY($event,'medicare_partb_date')" maxlength="7" [min]="partAB_MinDate">
                @if (leadDetailsForm.get('medicare_partb_date')?.invalid) {
                  <mat-error>Part B date can't be before 1965</mat-error>
                }
              </mat-form-field>
            </div>
            <div class="flex gap-4">
              <mat-form-field class="grow">
                <mat-label>Medicaid Number</mat-label>
                <input matInput formControlName="medicaid_number" [maxlength]="20" appDisableAutofill>
                @if (leadDetailsForm.value.medicaid_number.length == 20) {
                  <mat-hint>Max length is 20 characters</mat-hint>
                }
              </mat-form-field>
              <mat-form-field class="grow">
                <mat-label>Medicaid State</mat-label>
                <mat-select #State formControlName="medicaid_state">
                  <mat-option value="">None</mat-option>
                  @for (el of state_data; track el) {
                    <mat-option [value]="el.abbreviation">{{ el.abbreviation }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
            <!-- Lead Origination Data -->
            <h4 class="w-full user-profile-heading text-upper">Lead Origination Data</h4>
            <div class="flex gap-4" [ngClass]="checkForPermissions() ? 'not-allowed' : ''">
              <!-- Lead Source -->
              <mat-form-field class="grow">
                <mat-label>Lead Source</mat-label>
                <mat-select formControlName="source_vendor_id" [disabled]="checkForPermissions()">
                  @for (source of lead_vendor_data; track source) {
                    <mat-option value="{{source.lead_vendor_id}}">{{source?.lead_vendor_code + ' [' + source?.company + ']'}}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-form-field class="grow" [ngClass]="checkForPermissions() ? 'pointer-event' : ''"><!-- Advertising Code -->
                <mat-label>Advertising Code</mat-label>
                <input matInput formControlName="source_adcode" [readonly]="checkForPermissions()" [appInputMaxLength]>
              </mat-form-field>
            </div>
            <div class="flex gap-2" [ngClass]="checkForPermissions() ? 'not-allowed' : ''">
              <mat-form-field class="grow"><!-- B2B Partner ID -->
                <mat-label>Client/Partner/Affiliate</mat-label>
                <mat-select formControlName="b2bpartner_id" (selectionChange)="b2bChange($event.value)" [disabled]="checkForPermissions()">
                  <mat-option value=""></mat-option>
                  @for (b2b of b2bPartner_data; track b2b) {
                    <mat-option value="{{b2b.b2bpartner_id}}">{{b2b.company}}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              @if (lodEnabled) {
                <mat-form-field class="grow"><!-- Tracking Code -->
                  <mat-label>Tracking Code</mat-label>
                  <mat-select formControlName="source_code" [disabled]="checkForPermissions()">
                    @for (tCode of tracking_data; track tCode) {
                      @if (tCode.active == '1') {
                        <mat-option value="{{tCode.tracking_code}}">{{tCode.tracking_code}}</mat-option>
                      }
                    }
                    @if (lead_details && lead_details.source_code && tracking_data.length==0) {
                      <mat-option value="{{lead_details.source_code}}">{{lead_details.source_code}}</mat-option>
                    }
                  </mat-select>
                  @if (checkError('source_code', 'required')) {
                    <mat-error>Tracking Code is a required field.</mat-error>
                  }
                </mat-form-field>
              }
              @if (!lodEnabled) {
                <mat-form-field class="grow">
                  <mat-label>Tracking Code</mat-label>
                  <input matInput formControlName="source_code">
                </mat-form-field>
              }
            </div>
            <div class="flex gap-2" [ngClass]="checkForPermissions() ? 'not-allowed' : ''">
              <mat-form-field class="grow"><!-- Campaign ID -->
                <mat-label>Campaign</mat-label>
                <mat-select formControlName="campaign_id" (selectionChange)="changeCampaign($event.value)" [disabled]="checkForPermissions()">
                  <mat-option value=""></mat-option>
                  @for (camp of campaign_code; track camp) {
                    <mat-option value="{{camp.campaign_id}}">{{camp.campaign_code}}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-form-field class="grow"><!-- Campagn Segment ID -->
                <mat-label>Segment</mat-label>
                <mat-select formControlName="campaign_segment_code" [disabled]="checkForPermissions()">
                  @for (seg of campaign_segment; track seg) {
                    <mat-option value="{{seg.campaign_segment_code}}">{{seg.campaign_segment_code}}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
            <div class="flex gap-2" [ngClass]="checkForPermissions() ? 'not-allowed' : ''">
              <mat-form-field class="grow"><!-- Lead Type ID -->
                <mat-label>Lead Type</mat-label>
                <mat-select formControlName="lead_type_id" [disabled]="checkForPermissions()">
                  @for (type of lead_type; track type) {
                    <mat-option value="{{type.lead_type_id}}">{{type.lead_type_desc | titlecase}}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-form-field class="grow"><!-- Product Type -->
                <mat-label>Product Type</mat-label>
                <mat-select formControlName="product_type_id" [disabled]="checkForPermissions()">
                  @for (product of product_type; track product) {
                    <mat-option value="{{product.product_type_id}}">{{ product.product_type_name | titlecase }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
            <!-- Lead Contact Instuctions -->
            <!-- Lead Instruction Info -->
            <h4 class="user-profile-heading">Lead Instruction</h4>
            <div class="flex">
              @if (lead_details?.lead_instructions) {
                <ul>
                  @for (ins of lead_details?.lead_instructions; track ins) {
                    <li>{{ins.lead_instruction_text}}</li>
                  }
                </ul>
              }
            </div>
            <div class="flex gap-2 mt-1">
              <mat-form-field class="grow"><!-- Tags -->
                <mat-label>Tag</mat-label>
                <mat-chip-grid #chipList aria-label="Tag selection">
                  @for (tag of tags; track tag) {
                    <mat-chip-row (removed)="removeTag(tag)">
                      <span>{{tag.tag_name}}</span>
                      <button matChipRemove>
                        <mat-icon>cancel</mat-icon>
                      </button>
                    </mat-chip-row>
                  }
                  <input placeholder="" class="tag-input" #tagInput formControlName="tags" [matAutocomplete]="auto" [matChipInputFor]="chipList" [maxlength]="20" [matChipInputSeparatorKeyCodes]="separatorKeysCodes" (matChipInputTokenEnd)="addTag($event,'')">
                  @if (leadDetailsForm.value.tags.length == 20) {
                    <mat-error class="max-length-error">Max length is 20 characters</mat-error>
                  }
                </mat-chip-grid>
                <mat-autocomplete #auto="matAutocomplete" [displayWith]="getTagName" (optionSelected)="selected($event)">
                  @for (tag of filteredTags | async; track tag) {
                    <mat-option [value]="tag">{{tag.tag_name}}</mat-option>
                  }
                </mat-autocomplete>
              </mat-form-field>
              <button class="grow-0 text-nowrap" mat-flat-button color="primary" [disabled]="leadDetailsForm.value.tags ==''" (click)="addTag(leadDetailsForm.value.tags,'btn')">Add Tag</button>
            </div>
            <!-- Contact Permissions  -->
            <h4 class="user-profile-heading">Contact Permissions</h4>
            <p>I attest that I received client's permission to contact regarding <strong>Medicare Advantage</strong> and <strong>Medicare Part D</strong> via:</p>
            <div class="my-2">
              <mat-checkbox formControlName="do_not_email">Email</mat-checkbox>
              <mat-checkbox formControlName="do_not_call">Phone</mat-checkbox>
              <mat-checkbox formControlName="do_not_text">Text</mat-checkbox>
              <mat-checkbox formControlName="do_not_mail">Mail</mat-checkbox>
            </div>
            <span class="mr-2">Last updated by: {{ lead_details?.agent_fname + ' ' + lead_details?.agent_lname }}</span>
            <a class="linked-td" (click)="openPermissionHistory()" title="Show Permission History">Permission History</a>
            <!-- Lead Disposition -->
            <div>
              <h4 class="user-profile-heading">Latest Disposition</h4>
              <div class="flex gap-2">
                <mat-form-field class="grow">
                  <mat-label>Date</mat-label>
                  <input matInput formControlName="disposition_date" [matDatepicker]="disposition_date" appDate readonly class="not-allowed">
                  <mat-datepicker-toggle matSuffix [for]="disposition_date"></mat-datepicker-toggle>
                  <mat-datepicker #disposition_date></mat-datepicker>
                </mat-form-field>
                <mat-form-field class="grow">
                  <mat-label>Title</mat-label>
                  <input matInput formControlName="dispo_name" readonly class="not-allowed">
                </mat-form-field>
                <mat-form-field class="grow">
                  <mat-label>Description</mat-label>
                  <input matInput formControlName="dispo_log" readonly class="not-allowed">
                </mat-form-field>
              </div>
            </div>
          </div>
        </form>
      }


    </div>

    <div mat-dialog-actions align="end">
      @if (!isLoading) {
        <button mat-raised-button (click)="closeModal()">{{ hasChange ? 'Cancel' : 'Close' }}</button>
        @if (hasChange) {
          <button mat-flat-button (click)="updateLead('updateOnly')">Update</button>
        }
        @if (hasChange) {
          <button mat-flat-button (click)="updateLead('close')">Update + Close</button>
        }
      }
    </div>
  </div>
